import type { EventSummaryDto } from '@rw/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, QueryView } from '../../components/states';
import { Avatar, Button, Card } from '../../components/ui';
import { useEventList } from '../../hooks/queries';
import { RULE_LABEL, eventTiming, formatEventValue } from './event-format';
import { useT } from '../../i18n';

type Tab = 'discover' | 'mine' | 'hall';

export function EventsPage() {
  const q = useEventList();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('discover');
  const tr = useT();
  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        {tr('← Back')}
      </button>
      <div className="flex items-end justify-between gap-3">
        <h1 className="font-display text-4xl font-extrabold uppercase">{tr('Events')}</h1>
        {q.data?.canCreate ? (
          <Button size="sm" onClick={() => navigate('/events/new')} data-testid="create-event">
            {tr('+ Create')}
          </Button>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-ink-400">{tr('Races organised by runners — each with its own map, teams and rules.')}</p>
      <div className="mt-4 grid grid-cols-3 gap-1 rounded-2xl bg-ink-850 p-1">
        {(
          [
            ['discover', 'Discover'],
            ['mine', 'Mine'],
            ['hall', 'Hall of Fame'],
          ] as const
        ).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`events-tab-${t}`}
            className={`h-10 rounded-xl text-xs font-bold uppercase ${tab === t ? 'bg-volt text-ink-950' : 'text-ink-300'}`}
          >
            {tr(label)}
          </button>
        ))}
      </div>
      <QueryView query={q}>
        {(d) => (
          <>
            {d.invitations.length ? <Group title={tr("You're invited")} items={d.invitations} testId="event-invitations" /> : null}
            {tab === 'discover' ? (
              d.live.length + d.upcoming.length ? (
                <>
                  {d.live.length ? <Group title={tr('Live now')} items={d.live} /> : null}
                  {d.upcoming.length ? <Group title={tr('Coming up')} items={d.upcoming} /> : null}
                </>
              ) : (
                <EmptyState
                  icon="🏁"
                  title={tr('No public events yet')}
                  body={d.canCreate ? tr('Create one and invite your running friends.') : tr('New events from Runner Wars will show up here.')}
                />
              )
            ) : tab === 'mine' ? (
              d.mine.length ? (
                <Group title={tr('Joined or organised')} items={d.mine} />
              ) : (
                <EmptyState icon="🏃" title={tr("You're not in any event")} body={tr('Join one from Discover, or create your own.')} />
              )
            ) : d.past.length ? (
              <Group title={tr('Champions')} items={d.past} hall testId="hall-of-fame" />
            ) : (
              <EmptyState icon="🏆" title={tr('No finished events yet')} />
            )}
          </>
        )}
      </QueryView>
    </Page>
  );
}

export function EventRow({ e, hall }: { e: EventSummaryDto; hall?: boolean }) {
  const t = useT();
  return (
    <Link to={`/events/${e.id}`} className="block" data-testid="event-row">
      <Card className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {e.official ? <span className="rounded bg-gold/20 px-1.5 text-[10px] font-bold uppercase text-gold">{t('Official')}</span> : null}
            {e.approval !== 'APPROVED' ? (
              <span className={`shrink-0 rounded px-1.5 text-[10px] font-bold uppercase ${e.approval === 'PENDING' ? 'bg-ink-700 text-ink-200' : 'bg-enemy/20 text-enemy'}`}>
                {t(e.approval === 'PENDING' ? 'Waiting for approval' : 'Not approved')}
              </span>
            ) : null}
            {e.visibility === 'PRIVATE' ? <span className="text-xs" title={t('Private')}>🔒</span> : null}
            <span className="truncate font-display text-lg font-bold">{e.name}</span>
          </div>
          <div className="text-xs text-ink-400">
            {t(RULE_LABEL[e.rule])} · {eventTiming(e.status, e.startsAt, e.endsAt)} · {t(e.participants === 1 ? '{n} runner' : '{n} runners', { n: e.participants })}
            {e.teams ? ` · ${t('{n} teams', { n: e.teams })}` : ''}
          </div>
          <div className="mt-0.5 text-[11px] text-ink-500">
            {t('by {name}', { name: e.organizer.displayName })}
            {e.joined ? <span className="ml-1 font-semibold text-volt">· {t('Joined')}</span> : null}
            {e.isOrganizer ? <span className="ml-1 font-semibold text-gold">· {t('Organiser')}</span> : null}
          </div>
        </div>
        {hall && e.champion ? (
          <div className="flex items-center gap-2 text-right">
            <div>
              <div className="text-xs text-gold">🏆 {e.champion.user.displayName}</div>
              <div className="text-[11px] text-ink-500">{formatEventValue(e.rule, e.champion.value)}</div>
              {e.championTeam ? <div className="text-[11px] text-ink-400">{t('Team: {name}', { name: e.championTeam.team.name })}</div> : null}
            </div>
            <Avatar name={e.champion.user.displayName} color={e.champion.user.color} src={e.champion.user.avatarUrl} size="sm" />
          </div>
        ) : (
          <span className="text-xl text-ink-500">›</span>
        )}
      </Card>
    </Link>
  );
}

function Group({ title, items, hall, testId }: { title: string; items: EventSummaryDto[]; hall?: boolean; testId?: string }) {
  return (
    <>
      <div className="mb-2 mt-6 text-xs font-bold uppercase tracking-widest text-ink-400">{title}</div>
      <div className="space-y-2" data-testid={testId}>
        {items.map((e) => (
          <EventRow key={e.id} e={e} hall={hall} />
        ))}
      </div>
    </>
  );
}
